import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    AlertCircle, ArrowLeft, ClipboardCheck, Download, Loader2, Send,
    ShieldCheck, Car,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { DviAPI, JobCardAPI, AttachmentAPI, extractError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FindingCard } from "@/components/inspection/FindingCard";
import { SectionAccordion } from "@/components/inspection/SectionAccordion";
import { ShareLinkModal } from "@/components/inspection/ShareLinkModal";

const STATUS_PILL = {
    draft: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    in_progress: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    completed: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200",
    customer_review: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
    customer_approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    customer_rejected: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
};

function StatusBadge({ status }) {
    return (
        <span
            data-testid="dvi-status-badge"
            className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                STATUS_PILL[status] || STATUS_PILL.draft,
            )}
        >
            {(status || "draft").replace(/_/g, " ")}
        </span>
    );
}

function buildJobLabel(job) {
    if (!job) return "";
    const vehicle = job.vehicle_snapshot || {};
    const reg = vehicle.registration_number || vehicle.reg || "";
    const make = vehicle.make || "";
    const model = vehicle.model || "";
    const ref = job.job_number || job.reference_number || job._id || "";
    const veh = [make, model].filter(Boolean).join(" ");
    const reglabel = reg ? `[${reg}]` : "";
    return `${ref} · ${veh} ${reglabel}`.trim();
}

export default function InspectionWorkspacePage() {
    const { jobcard_id: jobcardId } = useParams();
    const navigate = useNavigate();
    const { user, permissions } = useAuth();
    const canWrite = permissions.includes("dvi.inspection.write");
    const canComplete = permissions.includes("dvi.inspection.complete");
    const canShare = permissions.includes("dvi.inspection.share");
    const canConvert = canWrite && permissions.includes("estimate:create");

    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [job, setJob] = useState(null);
    const [inspection, setInspection] = useState(null);
    const [template, setTemplate] = useState(null);
    const [findings, setFindings] = useState([]);
    const [attachmentsById, setAttachmentsById] = useState({});
    const [shareOpen, setShareOpen] = useState(false);
    const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
    const [estimateIdHint, setEstimateIdHint] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let alive = true;
        async function load() {
            try {
                const [jobResp, list] = await Promise.all([
                    JobCardAPI.get(jobcardId),
                    DviAPI.listForJobcard(jobcardId),
                ]);
                if (!alive) return;
                setJob(jobResp);

                const items = list.items || [];
                const activeOrCompleted = items.find(
                    (i) => !["customer_approved", "customer_rejected"].includes(i.status),
                );
                const latest = activeOrCompleted || items[0] || null;
                if (!latest) {
                    if (!alive) return;
                    setInspection(null); setFindings([]); setTemplate(null);
                    setAttachmentsById({});
                    return;
                }
                const [detail, atts] = await Promise.all([
                    DviAPI.getInspection(latest.id),
                    AttachmentAPI.list(jobcardId).catch(() => ({ items: [] })),
                ]);
                if (!alive) return;
                setInspection(detail);
                setFindings(detail.findings || []);
                if (detail.template_id) {
                    try {
                        const t = await DviAPI.getTemplate(detail.template_id);
                        if (alive) setTemplate(t);
                    } catch (e) {
                        if (alive) setTemplate(null);
                    }
                }
                if (!alive) return;
                setAttachmentsById(Object.fromEntries(
                    (atts.items || []).map((a) => [a.id, a]),
                ));
            } catch (err) {
                if (alive) toast.error(extractError(err));
            } finally {
                if (alive) setLoading(false);
            }
        }
        load();
        return () => { alive = false; };
    }, [jobcardId, refreshTick]);

    const refresh = () => setRefreshTick((t) => t + 1);

    const findingsByItem = Object.fromEntries(findings.map((f) => [
        f.item_id,
        {
            ...f,
            attachments: (f.attachment_ids || [])
                .map((aid) => attachmentsById[aid])
                .filter(Boolean),
        },
    ]));

    const requiredStats = useMemo(() => {
        if (!template) return { total: 0, filled: 0 };
        let total = 0; let filled = 0;
        for (const s of template.sections || []) {
            for (const it of s.items || []) {
                if (it.is_required) {
                    total++;
                    if (findingsByItem[it.id]?.severity) filled++;
                }
            }
        }
        return { total, filled };
    }, [template, findingsByItem]);

    const summary = inspection?.overall_summary || { green_count: 0, yellow_count: 0, red_count: 0, na_count: 0, total_items: 0 };
    const status = inspection?.status || "draft";
    const isTerminal = ["customer_approved", "customer_rejected"].includes(status);
    const isLocked = isTerminal;

    async function handleStart() {
        setStarting(true);
        try {
            await DviAPI.startForJobcard(jobcardId, {});
            toast.success("Inspection started");
            await refresh();
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setStarting(false);
        }
    }

    function handleFindingSaved(saved) {
        setFindings((curr) => {
            const exists = curr.find((f) => f.id === saved.id);
            if (exists) return curr.map((f) => (f.id === saved.id ? { ...f, ...saved } : f));
            return [...curr, saved];
        });
        // Re-fetch lightweight detail so overall_summary stays in sync
        DviAPI.getInspection(inspection.id).then((det) => {
            setInspection(det);
            setFindings(det.findings || []);
        }).catch(() => {});
    }

    function handleConverted(res) {
        setEstimateIdHint(res.estimate_id);
        // Reflect converted state on the finding
        setFindings((curr) => curr.map((f) => (
            f.id === res.finding_id
                ? { ...f, converted_to_estimate_line_id: res.estimate_line_id }
                : f
        )));
    }

    async function handleMarkComplete() {
        try {
            const updated = await DviAPI.patchInspection(inspection.id, { status: "completed" });
            setInspection((curr) => ({ ...curr, ...updated }));
            setConfirmCompleteOpen(false);
            toast.success("Inspection completed");
        } catch (err) {
            toast.error(extractError(err));
        }
    }

    const allRequiredFilled = requiredStats.total === 0 || requiredStats.filled === requiredStats.total;
    const canSubmitComplete = canComplete && (status === "in_progress" || status === "draft") && allRequiredFilled;
    const canGenerateShare = canShare && (status === "completed" || status === "customer_review");
    const canDownloadPdf = inspection && ["completed", "customer_review", "customer_approved", "customer_rejected"].includes(status);

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
            </div>
        );
    }

    if (!job) {
        return (
            <div className="space-y-3">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                    <ArrowLeft className="mr-1.5 h-4 w-4" />Back
                </Button>
                <Card className="p-6 text-center text-sm text-zinc-500">Job card not found.</Card>
            </div>
        );
    }

    const jobLabel = buildJobLabel(job);

    return (
        <div className="space-y-4 pb-20 sm:pb-4" data-testid="inspection-workspace">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2 h-7">
                        <Link to={`/jobs/${jobcardId}`} data-testid="inspection-back-to-job">
                            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                            Back to job card
                        </Link>
                    </Button>
                    <h1 className="flex items-center gap-2 text-2xl font-bold leading-tight tracking-tight">
                        <ClipboardCheck className="h-6 w-6 text-brand-redDeep" />
                        Inspection
                    </h1>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                        <Car className="h-3.5 w-3.5" />
                        <span data-testid="inspection-job-label">{jobLabel}</span>
                    </div>
                </div>

                {inspection ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={status} />
                        <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500" data-testid="inspection-required-progress">
                            {requiredStats.filled}/{requiredStats.total} required filled
                        </div>
                        <div className="flex items-center gap-1" data-testid="inspection-summary">
                            {summary.green_count > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                    ✓ {summary.green_count}
                                </span>
                            )}
                            {summary.yellow_count > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                    ! {summary.yellow_count}
                                </span>
                            )}
                            {summary.red_count > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                    ✕ {summary.red_count}
                                </span>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Empty state — no inspection yet (or only a terminal one) */}
            {!inspection ? (
                <Card className="p-6 text-center">
                    <ClipboardCheck className="mx-auto mb-3 h-10 w-10 text-zinc-300" />
                    <div className="text-base font-semibold">No active inspection</div>
                    <div className="mt-1 text-sm text-zinc-500">
                        Walk around the vehicle, then start a 30-point inspection.
                    </div>
                    <Button
                        className="mt-4" onClick={handleStart} disabled={starting || !canWrite}
                        data-testid="inspection-start-btn"
                    >
                        {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Start Inspection
                    </Button>
                </Card>
            ) : null}

            {/* Terminal banner — read-only */}
            {inspection && isTerminal ? (
                <Card className={cn(
                    "border-l-4 p-3 text-sm",
                    status === "customer_approved"
                        ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
                        : "border-rose-500 bg-rose-50 dark:bg-rose-900/20",
                )}>
                    <div className="flex items-center gap-2 font-semibold">
                        <ShieldCheck className="h-4 w-4" />
                        {status === "customer_approved"
                            ? `Customer approved ${findings.filter((f) => f.customer_decision === "approved").length} item${findings.filter((f) => f.customer_decision === "approved").length === 1 ? "" : "s"}`
                            : "Customer rejected this inspection"
                        }
                        {inspection.customer_signature_name
                            ? <span className="font-normal text-zinc-600 dark:text-zinc-300">
                                · by {inspection.customer_signature_name}
                              </span> : null}
                        {inspection.customer_reviewed_at
                            ? <span className="font-normal text-zinc-500">
                                · {new Date(inspection.customer_reviewed_at).toLocaleString()}
                              </span> : null}
                    </div>
                    <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                        Findings are locked. Start a new inspection if further work is needed.
                    </div>
                    <Button
                        size="sm" className="mt-2" onClick={handleStart} disabled={starting}
                        data-testid="inspection-start-new-btn"
                    >
                        Start New Inspection
                    </Button>
                </Card>
            ) : null}

            {/* Body — sectioned findings */}
            {inspection && template ? (
                <div className="space-y-3" data-testid="inspection-sections">
                    {(template.sections || []).map((section, idx) => (
                        <SectionAccordion
                            key={section.id}
                            section={section}
                            defaultOpen={idx === 0}
                            findingsByItem={findingsByItem}
                            locked={isLocked}
                            renderItem={(item) => (
                                <FindingCard
                                    key={item.id}
                                    item={item}
                                    section={section}
                                    finding={findingsByItem[item.id]}
                                    inspectionId={inspection.id}
                                    jobcardId={jobcardId}
                                    canWrite={canWrite && !isLocked}
                                    canConvert={canConvert && !isLocked}
                                    locked={isLocked}
                                    onFindingSaved={handleFindingSaved}
                                    onConverted={handleConverted}
                                />
                            )}
                        />
                    ))}
                </div>
            ) : null}

            {/* Estimate jump hint after a conversion */}
            {estimateIdHint ? (
                <Card className="border-emerald-300 bg-emerald-50 p-3 text-sm dark:border-emerald-700/40 dark:bg-emerald-900/20"
                      data-testid="estimate-hint">
                    Added to draft estimate ·{" "}
                    <Link
                        to={`/estimates/${estimateIdHint}`} className="font-semibold underline"
                        data-testid="estimate-hint-link"
                    >
                        Open estimate
                    </Link>
                </Card>
            ) : null}

            {/* Sticky action bar — bottom on mobile, top-right floating on desktop */}
            {inspection && !isTerminal ? (
                <div
                    className={cn(
                        "fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-white/95 px-3 py-2 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95",
                        "sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none",
                    )}
                >
                    <div className="mx-auto flex flex-wrap items-center justify-end gap-2 sm:max-w-none">
                        {!canSubmitComplete && status === "in_progress" ? (
                            <div className="mr-auto flex items-center gap-1 text-[11px] text-zinc-500 sm:mr-2">
                                <AlertCircle className="h-3 w-3" />
                                {requiredStats.total - requiredStats.filled} required item(s) remaining
                            </div>
                        ) : null}
                        <Button
                            type="button" size="sm" variant="outline" disabled={!canDownloadPdf}
                            onClick={() => window.open(DviAPI.pdfUrl(inspection.id), "_blank")}
                            data-testid="inspection-download-pdf-btn"
                        >
                            <Download className="mr-1.5 h-3.5 w-3.5" />
                            PDF
                        </Button>
                        <Button
                            type="button" size="sm" variant="outline" disabled={!canGenerateShare}
                            onClick={() => setShareOpen(true)}
                            data-testid="inspection-generate-share-btn"
                        >
                            <Send className="mr-1.5 h-3.5 w-3.5" />
                            {inspection.customer_share_token ? "Share link" : "Generate share link"}
                        </Button>
                        <Button
                            type="button" size="sm" disabled={!canSubmitComplete}
                            onClick={() => setConfirmCompleteOpen(true)}
                            data-testid="inspection-mark-complete-btn"
                            title={!allRequiredFilled ? `Required items remaining: ${requiredStats.total - requiredStats.filled}` : ""}
                        >
                            <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />
                            Mark Complete
                        </Button>
                    </div>
                </div>
            ) : null}

            {/* Terminal-state action bar — download PDF only */}
            {inspection && isTerminal && canDownloadPdf ? (
                <div className="flex justify-end">
                    <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => window.open(DviAPI.pdfUrl(inspection.id), "_blank")}
                        data-testid="inspection-download-pdf-btn"
                    >
                        <Download className="mr-1.5 h-3.5 w-3.5" />
                        Download PDF
                    </Button>
                </div>
            ) : null}

            {/* Share Link Modal */}
            {inspection ? (
                <ShareLinkModal
                    open={shareOpen}
                    onOpenChange={setShareOpen}
                    inspection={inspection}
                    jobLabel={jobLabel}
                    onShareUpdated={(s) => setInspection((curr) => ({ ...curr, ...s, customer_share_token: s.token }))}
                />
            ) : null}

            {/* Mark Complete confirmation */}
            <Dialog open={confirmCompleteOpen} onOpenChange={setConfirmCompleteOpen}>
                <DialogContent data-testid="inspection-confirm-complete-modal">
                    <DialogHeader>
                        <DialogTitle>Finalize inspection?</DialogTitle>
                        <DialogDescription>
                            Inspection will be marked complete. You can still edit findings until the
                            customer reviews them.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmCompleteOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleMarkComplete} data-testid="inspection-confirm-complete-btn">
                            Continue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
